import { supabase } from '@foundation/altzero_base/base/utils/supabaseClient';
import type { DashboardMetrics, OutcomeCount, StatusCount, DailyCount, SymptomCount } from '../types/dashboard.types';
import type { AssessmentPayload } from '../../incident-logging/types/incident.types';
import type { OutcomeKey, CaseStatus } from '../../assessment-history/types/assessmentHistory.types';

const OUTCOME_KEYS: OutcomeKey[] = ['emergency', 'possible_concussion', 'monitoring', 'no_signs'];
const STATUS_KEYS: CaseStatus[] = ['new', 'under_review', 'referred_to_doctor', 'awaiting_clearance', 'cleared_to_play', 'closed'];

export const fetchDashboardMetrics = async (): Promise<DashboardMetrics> => {
  // Fetch all assessment incidents (we aggregate client-side for flexibility)
  const { data: incidents, error } = await supabase
    .schema('charlie')
    .from('incident_logs')
    .select('outcome, red_flags_detected, current_status, assessment_data, created_at')
    .eq('incident_type', 'assessment')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch dashboard metrics:', error);
    throw new Error(error.message);
  }

  const rows = incidents ?? [];
  const totalAssessments = rows.length;

  // Red flag count
  const redFlagCount = rows.filter((r) => r.red_flags_detected).length;

  // Outcome breakdown
  const outcomeTally: Record<string, number> = {};
  for (const key of OUTCOME_KEYS) outcomeTally[key] = 0;
  for (const r of rows) {
    if (r.outcome && r.outcome in outcomeTally) outcomeTally[r.outcome]++;
  }
  const outcomeCounts: OutcomeCount[] = OUTCOME_KEYS.map((k) => ({ outcome: k, count: outcomeTally[k] }));

  // Status breakdown
  const statusTally: Record<string, number> = {};
  for (const key of STATUS_KEYS) statusTally[key] = 0;
  for (const r of rows) {
    const s = (r.current_status ?? 'new') as string;
    if (s in statusTally) statusTally[s]++;
  }
  const statusCounts: StatusCount[] = STATUS_KEYS.map((k) => ({ status: k, count: statusTally[k] }));

  // Daily counts (last 30 days) — use local dates for consistency
  const now = new Date();
  const toLocalDateStr = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
  const dayMap = new Map<string, number>();
  for (let i = 0; i < 30; i++) {
    const d = new Date(thirtyDaysAgo);
    d.setDate(d.getDate() + i);
    dayMap.set(toLocalDateStr(d), 0);
  }
  for (const r of rows) {
    // Convert created_at to local date string
    const localDate = toLocalDateStr(new Date(r.created_at));
    if (dayMap.has(localDate)) {
      dayMap.set(localDate, (dayMap.get(localDate) ?? 0) + 1);
    }
  }
  const dailyCounts: DailyCount[] = Array.from(dayMap.entries()).map(([date, count]) => ({ date, count }));

  // This week / this month
  const weekStart = new Date(now);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay() + (weekStart.getDay() === 0 ? -6 : 1));
  weekStart.setHours(0, 0, 0, 0);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  let assessmentsThisWeek = 0;
  let assessmentsThisMonth = 0;
  for (const r of rows) {
    const d = new Date(r.created_at);
    if (d >= weekStart) assessmentsThisWeek++;
    if (d >= monthStart) assessmentsThisMonth++;
  }

  // Top symptoms / red flags / observable signs
  const redFlagTally = new Map<string, number>();
  const signsTally = new Map<string, number>();
  const symptomTally = new Map<string, number>();
  let totalDuration = 0;
  let durationCount = 0;

  for (const r of rows) {
    const ad = r.assessment_data as AssessmentPayload | null;
    if (!ad) continue;

    for (const flag of ad.selected_red_flags ?? []) {
      redFlagTally.set(flag, (redFlagTally.get(flag) ?? 0) + 1);
    }
    for (const sign of ad.selected_observable_signs ?? []) {
      signsTally.set(sign, (signsTally.get(sign) ?? 0) + 1);
    }
    for (const sym of ad.selected_self_symptoms ?? []) {
      symptomTally.set(sym, (symptomTally.get(sym) ?? 0) + 1);
    }

    if (ad.started_at && ad.completed_at) {
      const dur = (new Date(ad.completed_at).getTime() - new Date(ad.started_at).getTime()) / 1000;
      if (dur > 0 && dur < 3600) {
        totalDuration += dur;
        durationCount++;
      }
    }
  }

  const toSorted = (m: Map<string, number>): SymptomCount[] =>
    Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

  return {
    totalAssessments,
    redFlagCount,
    outcomeCounts,
    statusCounts,
    dailyCounts,
    topRedFlags: toSorted(redFlagTally),
    topObservableSigns: toSorted(signsTally),
    topSymptoms: toSorted(symptomTally),
    avgDurationSeconds: durationCount > 0 ? Math.round(totalDuration / durationCount) : null,
    assessmentsThisWeek,
    assessmentsThisMonth,
  };
};
